import React, { useContext, useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { AuthContext } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";
import { useNavigate, Link } from "react-router-dom";
import { FaPhoneAlt, FaEnvelope, FaWhatsapp, FaInstagram, FaFacebookF } from "react-icons/fa";

import "../../styles/Individual.css";

const BlogList = lazy(() => import("../../components/BlogList"));

const tipsImages = [
  "/images/solar1.jpg",
  "/images/solar2.jpg",
  "/images/solar3.jpg",
  "/images/solar4.jpg",
  "/images/3.png",
];

const tools = [
  { path: "/solarsizer", icon: "🔋", label: "Solar Sizing" },
  { path: "/solarmarket", icon: "🏪", label: "Solar Market" },
  { path: "/solartools", icon: "🔧", label: "Solar Tools" },
  { path: "/error-codes", icon: "⚙️", label: "Error Codes" },
  { path: "/solar-resources", icon: "📚", label: "Resources" },
  { path: "/solar-quiz", icon: "🎮", label: "Solar Quizes" },
  { path: "/solarseminar", icon: "🎓", label: "Solar Seminar" },
  { path: "/ai-assistant", icon: "💬", label: "Chat" },



  /*
  { path: "/service-centers", icon: "🔧", label: "Servicing" },
  { path: "/calculator/sizing", icon: "🧮", label: "Full Sizing" },
    
    { path: "/bookings", icon: "📅", label: "Bookings" },
    { path: "/installers", icon: "🧰", label: "Find Installers" },
    { path: "/vendors", icon: "🏪", label: "Vendors Near You" },
    { path: "/blogs", icon: "📚", label: "Blog & Guides" },
    { path: "/resources", icon: "🗂️", label: "Downloads" },
    { path: "/listings", icon: "🔖", label: "Listings" },
    { path: "/profiles", icon: "🧑‍🔧", label: "Installer Profiles" },
    */
];

export default function DashboardVendor() {
  const { user, logout, token } = useContext(AuthContext);
  const { unreadCount } = useContext(NotificationContext);
  const [slideIndex, setSlideIndex] = useState(0);
  const [cardIndex, setCardIndex] = useState(0);
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const totalCards = 2;

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  // Combined carousel intervals
  useEffect(() => {
    const cardInterval = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % totalCards);
    }, 5000);
    const slideInterval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % tipsImages.length);
    }, 4000);
    return () => {
      clearInterval(cardInterval);
      clearInterval(slideInterval);
    };
  }, []);

  useEffect(() => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({
        left: cardIndex * containerWidth,
        behavior: "smooth",
      });
    }
  }, [cardIndex]);

  // Deferred location fetch
  useEffect(() => {
    if (!user || !token || (user?.coordinates?.lat && user?.coordinates?.lon)) return;

    const timeoutId = setTimeout(() => {
      const updateCoordinates = async (lat, lon) => {
        try {
          await fetch("https://globisun-app.onrender.com/api/auth/update-coordinates", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ lat, lon }),
          });
        } catch (err) {}
      };

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => updateCoordinates(position.coords.latitude, position.coords.longitude),
          async () => {
            try {
              const res = await fetch("https://ipapi.co/json/");
              const data = await res.json();
              if (data.latitude && data.longitude) updateCoordinates(data.latitude, data.longitude);
            } catch (err) {}
          },
          { timeout: 5000, maximumAge: 300000 }
        );
      } else {
        fetch("https://ipapi.co/json/")
          .then(res => res.json())
          .then(data => {
            if (data.latitude && data.longitude) updateCoordinates(data.latitude, data.longitude);
          })
          .catch(() => {});
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [user, token]);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  // Deferred inbox fetch
  useEffect(() => {
    if (!token) return;
    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch("https://globisun-app.onrender.com/api/chats/inbox", {
          headers: { Authorization: `Bearer ${token}` },
        });
        await res.json();
      } catch (err) {}
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [token]);

  if (!user) return null;

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="brand">⚡ Globisun</h1>
        </div>
        <nav className="nav-links">
          <Link to="/calculator/load">🔌 Load Calculator</Link>
          <Link to="/calculator/panels">🔋 Panel Sizing Tool</Link>
          <Link to="/calculator/sizing">🧮 Full Solar Sizing</Link>
          <Link to="/notifications">🌤️ Solar Weather Alerts</Link>
          <Link to="/installers">🧰 Find Installers</Link>
          <Link to="/vendors">🏪 Vendors Near You</Link>
          <Link to="/blogs">📚 Blog & Solar Guides</Link>
          <Link to="/resources">🗂️ Downloads & Resources</Link>
          <hr />
          <Link to="/listings">🔖 Listings</Link>
          <Link to="/profiles">🧑‍🔧 Profiles</Link>
          <Link to="/bookings">📅 My Bookings</Link>
          <Link to="/inbox">
            💬 Chat{" "}
            {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
          </Link>
        </nav>
      </aside>

      <main className="dashboard-content wide">
        <header className="dashboard-header">
          <div className="social-icons" style={{ textAlign: "center", marginBottom: "10px" }}>
            <a href="tel:+2348131299300" target="_blank" rel="noopener noreferrer">
              <FaPhoneAlt className="social-icon" />
            </a>
            <a href="mailto:globisunsolar@gmail.com" target="_blank" rel="noopener noreferrer">
              <FaEnvelope className="social-icon" />
            </a>
            <a href="https://wa.me/2348131299300" target="_blank" rel="noopener noreferrer">
              <FaWhatsapp className="social-icon" />
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
              <FaInstagram className="social-icon" />
            </a>
            <a href="https://facebook.com/profile.php?id=61575518742736" target="_blank" rel="noopener noreferrer">
              <FaFacebookF className="social-icon" />
            </a>
          </div>
          <h2>Welcome, {user.name} <span className="wave">👋</span></h2>
          <p>Power your solar decisions with the right tools 🔧</p>
        </header>

        <section className="hero-section">
          <div className="slide-carousel-wrapper">
            <div className="slide-carousel-track" style={{ transform: `translateX(-${slideIndex * 100}%)` }}>
              {tipsImages.map((img, idx) => (
                <img key={idx} src={img} alt={`Solar Tip ${idx + 1}`} className="slide-image" loading="lazy" decoding="async" />
              ))}
            </div>
          </div>
        </section>

        <section className="tool-grid">
          {tools.map((tool, index) => (
            <div key={index} className="tool-wrapper">
              <div className="tool-card" onClick={() => handleNavigate(tool.path)}>
                <span className="tool-icon">{tool.icon}</span>
              </div>
              <div className="tool-label">{tool.label}</div>
            </div>
          ))}
        </section>

        <section className="sliding-cards-section">
          <div className="carousel-track-wrapper" ref={carouselRef}>
            <div className="carousel-card">
              <img src="/images/" alt="" className="card-icon" loading="lazy" />
              <div className="card-content">
                <h3>Courses</h3>
                <p>Learn valuable skills and gain expertise with top-rated courses.</p>
                <a href="/solar-resources" className="card-link">Explore now →</a>
              </div>
            </div>
            <div className="carousel-card">
              <img src="/icons/consultation-icon.svg" alt="" className="card-icon" loading="lazy" />
              <div className="card-content">
                <h3>Consultation</h3>
                <p>Get personalized help and advice from experts.</p>
                <a href="https://wa.me/2348131299300" className="card-link">Book now →</a>
              </div>
            </div>
          </div>
        </section>

        <Suspense fallback={<div>Loading blogs...</div>}>
          <BlogList />
        </Suspense>

        <div className="bottom-logout">
          <button className="logout-btn" onClick={logout}>🚪 Logout</button>
        </div>
      </main>
    </div>
  );
}
